import React, { useState, useEffect, useCallback } from "react";
import Modal from "react-modal";
import moment from "moment";
import useApi from "../hooks/useApi";
import { apiUrls } from "../utils/api/apiUrls";
import { toast } from "react-toastify";

const ReworkHistoryModal = ({
  isModalVisible,
  setIsModalVisible,
  voucherId,
}) => {
  const { data: historyData = [], apiCall, loading } = useApi();
  const [historyList, setHistoryList] = useState([]);

  const fetchReworkHistory = useCallback(() => {
    apiCall({
      url: apiUrls.dmsService.getReworkHistory + voucherId,
      method: "get",
      onSuccess: (data) => {
        if (Array.isArray(data)) {
          setHistoryList(data);
        } else {
          setHistoryList([]);
        }
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message || "Failed to fetch rework history"
        );
        setHistoryList([]);
      },
    });
  }, [apiCall, voucherId]);

  useEffect(() => {
    if (isModalVisible.isOpen && voucherId) {
      fetchReworkHistory();
    }
  }, [isModalVisible.isOpen, voucherId, fetchReworkHistory]);

  useEffect(() => {
    if (historyData && Array.isArray(historyData)) {
      setHistoryList(historyData);
    }
  }, [historyData]);

  const onRequestClose = () => {
    setIsModalVisible({ ...isModalVisible, isOpen: false });
  };

  const getStatusColor = (status) => {
    if (status === "COMPLETED") {
      return "text-[var(--successGreen)]";
    } else if (status === "RESUBMITTED") {
      return "text-[var(--pendingYellow)]";
    } else {
      return "text-[#FF8C00]";
    }
  };

  return (
    <div className="flex justify-center items-center w-full">
      <Modal
        isOpen={isModalVisible.isOpen}
        onRequestClose={onRequestClose}
        ariaHideApp={false}
        style={{
          content: {
            display: "flex",
            flexDirection: "column",
            borderRadius: "1rem",
            background: "#fff",
            padding: "1.5rem",
            width: "90%",
            maxWidth: "50rem",
            maxHeight: "90vh",
            inset: "0px",
            position: "relative",
            overflow: "auto",
          },
          overlay: {
            background: "#000000d6",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99,
          },
        }}
      >
        <div className="w-full">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <div className="text-[2rem] font-semibold">
              Rework History - Voucher {voucherId}
            </div>
            <div
              className="text-[3rem] cursor-pointer text-red-500 ml-4"
              onClick={onRequestClose}
            >
              ×
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-8 text-[1.8rem]">
              Loading rework history...
            </div>
          )}

          {/* Empty State */}
          {!loading && historyList.length === 0 && (
            <div className="text-center py-8 text-[1.8rem] text-gray-500">
              No rework history found for this voucher.
            </div>
          )}

          {/* History List */}
          {!loading && historyList.length > 0 && (
            <div className="space-y-4">
              {historyList.map((history, index) => (
                <div
                  key={index}
                  className="border border-gray-300 rounded-lg p-4 bg-gray-50"
                >
                  {/* Status and Level */}
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div
                        className={`text-[1.6rem] font-semibold ${getStatusColor(
                          history.status
                        )}`}
                      >
                        {history.status || "REQUESTED"}
                      </div>
                      <div className="text-[1.4rem] text-gray-600 mt-1">
                        Requested by: {history.requestedByLevel || "Unknown"} (
                        {history.requestedByUser || "Unknown"})
                      </div>
                    </div>
                    {history.subVoucherId && (
                      <div className="text-[1.2rem] text-gray-500">
                        Sub-Voucher: {history.subVoucherId}
                      </div>
                    )}
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-2 mb-3 text-[1.4rem]">
                    <div>
                      <span className="font-semibold">Requested Date:</span>{" "}
                      {history.requestedDate
                        ? moment(history.requestedDate).format(
                            "DD MMM YYYY, HH:mm"
                          )
                        : "-"}
                    </div>
                    {history.resubmittedDate && (
                      <div>
                        <span className="font-semibold">Resubmitted Date:</span>{" "}
                        {moment(history.resubmittedDate).format(
                          "DD MMM YYYY, HH:mm"
                        )}
                      </div>
                    )}
                  </div>

                  {/* Rework Comment */}
                  {history.reworkComment && (
                    <div className="mb-3">
                      <div className="text-[1.4rem] font-semibold mb-1">
                        Rework Comment:
                      </div>
                      <div className="text-[1.4rem] bg-yellow-50 border-l-4 border-yellow-400 p-2 rounded">
                        {history.reworkComment}
                      </div>
                    </div>
                  )}

                  {/* Voucher Details */}
                  <div className="border-t border-gray-300 pt-3 mt-3">
                    <div className="text-[1.4rem] font-semibold mb-2">
                      Voucher Details:
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[1.3rem]">
                      <div>
                        <span className="font-semibold">Head:</span>{" "}
                        {history.voucherHead || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Amount:</span> ₹
                        {history.amount
                          ? Number(history.amount).toFixed(2)
                          : "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Approved Amount:</span>{" "}
                        ₹
                        {history.approvedAmount
                          ? Number(history.approvedAmount).toFixed(2)
                          : "-"}
                      </div>
                      {history.voucherComment && (
                        <div className="col-span-2">
                          <span className="font-semibold">Comment:</span>{" "}
                          {history.voucherComment}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Status at Time of Rework */}
                  <div className="border-t border-gray-300 pt-3 mt-3">
                    <div className="text-[1.4rem] font-semibold mb-2">
                      Status at Time of Rework:
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-[1.3rem]">
                      <div>
                        <span className="font-semibold">L1:</span>{" "}
                        {history.l1Status || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">L2:</span>{" "}
                        {history.l2Status || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">A/C:</span>{" "}
                        {history.acStatus || "-"}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Close Button */}
          <div className="w-full flex justify-end mt-6">
            <button
              onClick={onRequestClose}
              className="px-6 py-2 bg-gray-200 text-gray-800 rounded text-[1.6rem] font-semibold hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ReworkHistoryModal;
